import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	BindingEngine,
	computedFrom,
	customElement,
	observable,
	PLATFORM,
	useView,
} from 'aurelia-framework';

import { FluentRuleCustomizer } from 'aurelia-validation';
import { Field } from '~/components/field/common/field';
import { Form } from '~/components/form/form';
import { DateFormatValueConverter } from '~/converters/date-format';
import {
	commonFieldProperties,
	registerComponent,
} from '~/forms/customisation-editor/property-meta';
import { FieldModel } from '~/models/form-model';
import { DateTimeProperties } from '~/utils/datetime/date-time-properties';
import { LocalizedDateTimeFormats } from '~/utils/datetime/localized-date-time-formats';
import { FlatpickrUtils } from '~/utils/flatpickr-utils';
import { CustomValidationRuleName } from '~/validation/custom-validation-rules';

const componentTagName = 'a-datepicker';

registerComponent(componentTagName, {
	name: 'Datepicker',
	requirePath: PLATFORM.moduleName('components/field/common/datepicker'),
	props: commonFieldProperties,
	isSelectable: true,
});

@autoinject
@customElement(componentTagName)
@useView(PLATFORM.moduleName('./datepicker.html'))
export class Datepicker extends Field {
	private flatpickrUtils: FlatpickrUtils;

	private isAttached: boolean = false;

	/**
	 * Set on datepicker.html template via "ref".
	 */
	@observable({ changeHandler: 'refInputElChanged' })
	private refInputEl: HTMLInputElement;

	/**
	 * An optional configuration options to customize the FlatpickrUtils.
	 * Please reference the usage is {@link filter-option.html} and {@link api-audit.ts}
	 */
	@bindable
	private datetimeProperties?: DateTimeProperties;

	constructor(
		public element: Element,
		protected parentForm: Form,
		readonly eventAggregator: EventAggregator,
		protected bindingEngine: BindingEngine,
	) {
		super(Field.TYPE.DATEPICKER, element, parentForm, eventAggregator);
		this.isDisableInputMasks = true;
	}

	protected setFieldValidation(
		field: Field,
		fluentRulesCustomizer: FluentRuleCustomizer<Field, any>,
	): void {
		super.setFieldValidation(field, fluentRulesCustomizer);
		fluentRulesCustomizer.satisfiesRule(
			CustomValidationRuleName.VALID_DATE,
		);
	}

	private tryInitDatepicker = (): void => {
		if (!this.isAttached || !this.refInputEl || this.flatpickrUtils) {
			// Ignore until isAttached or until element exists
			// -or-
			// Ignore if already initialized
			return;
		}

		const enableSecondsProp: boolean =
			this.datetimeProperties?.format ===
			LocalizedDateTimeFormats.DateTimeWithSeconds;
		const enableTimeProp: boolean =
			this.datetimeProperties?.format ===
				LocalizedDateTimeFormats.DateTime || enableSecondsProp;

		this.flatpickrUtils = new FlatpickrUtils(this.refInputEl, {
			enableTime: enableTimeProp,
			enableSeconds: enableSecondsProp,
			minDate: this.datetimeProperties?.minDate,
			onChange: this.onFlatpickrChange,
		});

		// NOTE(Jake): 2018-10-04 - WEBUI-1017
		// Flatpickr changes the <input> field attribute "type=hidden" and
		// then creates a new input field. If we're currently focused on the
		// hidden input field, change to the non-hidden one created by Flatpickr.
		if (document.activeElement === this.refInputEl) {
			this.flatpickrUtils.flatpickr._input.focus();
		}

		// Update the refInputElem to the flatpickr one visible to the user
		this.refInputElem = this.flatpickrUtils.flatpickr._input;

		this.valueChanged(this.value, '');

		if (this.fieldModel) {
			this.setFieldModelAttributes(this.fieldModel);
		}
	};

	private destroyDatepicker = (): void => {
		if (!this.flatpickrUtils) {
			// Ignore if already destroyed
			return;
		}
		this.refInputElem = undefined;
		this.removeFieldModelAttributes();
		this.flatpickrUtils.unmount();
		this.flatpickrUtils = undefined;
	};

	private refInputElChanged(
		newEl: HTMLInputElement | null,
		oldEl: HTMLInputElement | null,
	): void {
		if (oldEl) {
			this.destroyDatepicker();
		}
		if (newEl) {
			this.tryInitDatepicker();
		}
	}

	private onFlatpickrChange = (value: string | undefined): void => {
		this.value = value;
	};

	public attached() {
		super.attached();
		this.isAttached = true;

		this.tryInitDatepicker();
	}

	protected detached() {
		this.isAttached = false;
		this.destroyDatepicker();
	}

	/**
	 * Update the Flatpickr's <input> element based on FieldModel name
	 */
	private setFieldModelAttributes(fieldModel: FieldModel) {
		// NOTE(Jake): 2018-10-08
		// Add data-field-id to the visible input created by Flatpickr.
		// This allows E2E tests to check `document.activeElement.getAttribute('data-field-id')`.
		if (this.flatpickrUtils && this.flatpickrUtils.flatpickr._input) {
			this.flatpickrUtils.flatpickr._input.setAttribute(
				'data-field-id',
				fieldModel.name,
			);
			this.flatpickrUtils.flatpickr._input.setAttribute(
				'title',
				this.fieldTitle,
			);
		}
	}

	private removeFieldModelAttributes() {
		if (this.flatpickrUtils && this.flatpickrUtils.flatpickr._input) {
			this.flatpickrUtils.flatpickr._input.removeAttribute(
				'data-field-id',
			);
		}
	}

	protected fieldModelChanged(fieldModel: FieldModel) {
		super.fieldModelChanged(fieldModel);
		if (fieldModel) {
			this.setFieldModelAttributes(fieldModel);
		} else {
			this.removeFieldModelAttributes();
		}
	}

	protected valueChanged(value: string, oldValue: string) {
		// SR-25803: When date field is manually cleared by user, the changed value became undefined
		// undefined would cause blank object {} being sent to backend during save operation
		// we should always set new value to empty string when its null or undefined
		value = value ?? '';
		super.valueChanged(value, oldValue);
		if (!this.flatpickrUtils) {
			return;
		}
		const flatpicker = this.flatpickrUtils.flatpickr;
		// When value changed by e.g. end-field, we have to manually call
		// flatpicker.setDate() so that the value can be refected on the input
		// field generated by flatpicker
		flatpicker.setDate(DateFormatValueConverter.toViewStatic(value));
		if (!value) {
			if (flatpicker._input.value) {
				flatpicker.setDate('', false);
				// NOTE(Jake): 2018-09-11 - WEBUI-725
				// Reset picker to current date if empty
				flatpicker.jumpToDate(undefined);
			}
		}
	}

	private onIconClick = (): void => {
		if (this.flatpickrUtils.flatpickr) {
			this.flatpickrUtils.flatpickr.open();
		}
	};

	@computedFrom('value')
	get displayValue() {
		// NOTE(Jake): 2018-09-05
		// If displayValue() is removed, the date will display using the "DateFormatValueConverter.apiFormat"
		// for a split second after saving.
		// Logger.log('displayValue', this.value, 'converted to:', DateFormatValueConverter.toViewStatic(this.value));
		return DateFormatValueConverter.toViewStatic(this.value);
	}
}
